import { NoticeError } from "@/lib/i18n"
import { LosslessNumber, isLosslessNumber, parse, stringify } from "lossless-json"
import { asArray, asString, isRecord } from "./utils"

/**
 * The installed set parsed losslessly: every number keeps its exact source
 * text, so a save sends untouched artifacts back byte-for-byte in value
 * (decimals beyond float precision included, #2770/#3171). Only the string
 * cells an operator edited change.
 */
export interface LosslessSet {
  readonly version: number
  /** Tenant artifacts: the candidate layer a save sends. */
  readonly candidate: readonly Record<string, unknown>[]
  /** Registry base artifacts: preflight context, never sent. */
  readonly base: readonly Record<string, unknown>[]
  /** The installed editor layout sidecar (#3230), exact, or null when the cell sends none. */
  readonly layout: Record<string, unknown> | null
}

function numeric(value: unknown): number {
  if (isLosslessNumber(value)) return Number(value.value)
  if (typeof value === "number") return value
  throw new NoticeError({ key: "decision.invalidVersion" })
}

export function parseLosslessSet(text: string): LosslessSet {
  const doc = parse(text)
  if (!isRecord(doc)) throw new NoticeError({ key: "decision.invalidDefinitionResponse" })
  // The cell names its read-only registry base; a save excludes exactly these ids.
  const base = new Set(asArray(doc.baseArtifactIds).flatMap((id) => (typeof id === "string" ? [id] : [])))
  const candidate: Record<string, unknown>[] = []
  const baseLayer: Record<string, unknown>[] = []
  for (const a of asArray(doc.artifacts)) {
    if (!isRecord(a)) continue
    const id = asString(a.artifactId)
    if (id && base.has(id)) baseLayer.push(a)
    else candidate.push(a)
  }
  return { version: numeric(doc.version), candidate, base: baseLayer, layout: isRecord(doc.layout) ? doc.layout : null }
}

/** A cell read as an own property only, so a column named `__proto__` is data, never the prototype. */
function own(row: Record<string, unknown>, column: string): unknown {
  return Object.hasOwn(row, column) ? row[column] : undefined
}

/** Declared input and output column ids, plus the row description. */
function editableColumns(table: Record<string, unknown>): Set<string> {
  const ids = [...asArray(table.inputs), ...asArray(table.outputs)].flatMap((c) => (isRecord(c) && typeof c.id === "string" ? [c.id] : []))
  return new Set([...ids, "_description"])
}

/**
 * Whether the editor changed anything this editor does not save: rows added,
 * removed or reordered, columns changed, or the hit policy. Install is
 * blocked while true, so nothing is ever dropped silently.
 */
export function structureChanged(original: unknown, edited: unknown): boolean {
  if (!isRecord(original) || !isRecord(edited)) return false
  const rowIds = (t: Record<string, unknown>) => asArray(t.rules).map((r) => (isRecord(r) ? r._id : undefined))
  const columnIds = (t: Record<string, unknown>, key: string) => asArray(t[key]).map((c) => (isRecord(c) ? `${String(c.id)}|${String(c.field ?? "")}|${String(c.name ?? "")}` : ""))
  return (
    JSON.stringify(rowIds(original)) !== JSON.stringify(rowIds(edited)) ||
    JSON.stringify(columnIds(original, "inputs")) !== JSON.stringify(columnIds(edited, "inputs")) ||
    JSON.stringify(columnIds(original, "outputs")) !== JSON.stringify(columnIds(edited, "outputs")) ||
    (edited.hitPolicy !== undefined && edited.hitPolicy !== original.hitPolicy)
  )
}

/** One decision table's edited cells: row `_id` → column id → ZEN text. */
export type TableEdit = Readonly<Record<string, Readonly<Record<string, string>>>>

export interface AppliedEdit {
  readonly artifact: Record<string, unknown>
  readonly changedCells: number
}

function deepClone<T>(value: T): T {
  return parse(stringify(value) ?? "null") as T
}

/**
 * The rule artifact with edited table cells written in place. Only string
 * cells of existing rows and declared input/output columns change; row order,
 * ids, descriptions, other nodes, layout and every other member are kept.
 */
export function applyTableEdits(rule: Record<string, unknown>, edits: Readonly<Record<string, TableEdit>>): AppliedEdit {
  const artifact = deepClone(rule)
  let changedCells = 0
  const payload = isRecord(artifact.payload) ? artifact.payload : null
  const graph = payload && isRecord(payload.graph) ? payload.graph : null
  for (const node of asArray(graph?.nodes)) {
    if (!isRecord(node) || node.type !== "decisionTableNode") continue
    const nodeId = asString(node.id) ?? ""
    const edit = Object.hasOwn(edits, nodeId) ? edits[nodeId] : undefined
    if (!edit || !isRecord(node.content)) continue
    const columns = editableColumns(node.content)
    for (const row of asArray(node.content.rules)) {
      if (!isRecord(row)) continue
      const rowId = asString(row._id) ?? ""
      const rowEdit = Object.hasOwn(edit, rowId) ? edit[rowId] : undefined
      if (!rowEdit) continue
      for (const column of Object.keys(rowEdit)) {
        const text = rowEdit[column]
        if (!columns.has(column) || typeof text !== "string") continue
        const current = own(row, column)
        if (current !== undefined && typeof current !== "string") continue
        if ((current ?? "") === text) continue
        // An own data property even for a column named `__proto__`: never the inherited setter.
        Object.defineProperty(row, column, { value: text, writable: true, enumerable: true, configurable: true })
        changedCells += 1
      }
    }
  }
  return { artifact, changedCells }
}

/** The candidate layer with one artifact replaced (matched by id), order preserved. */
export function withArtifact(candidate: readonly Record<string, unknown>[], replacement: Record<string, unknown>): Record<string, unknown>[] {
  const id = asString(replacement.artifactId)
  return candidate.map((a) => (asString(a.artifactId) === id ? replacement : a))
}

/**
 * The exact PUT /v1/defs body text. `layout` sends a whole sidecar; omitting it keeps the installed one.
 * A geometry-only save sends the candidate layer exactly as read, so semantics and hashes are unchanged.
 */
export function installBody(scope: { orgId: string; environmentId: string }, candidate: readonly Record<string, unknown>[], expectedVersion: number, layout?: Record<string, unknown>): string {
  return stringify({ scope, artifacts: candidate, expectedVersion: new LosslessNumber(String(expectedVersion)), ...(layout ? { layout } : {}) }) ?? "{}"
}

/** A canvas position as exact JSON: whole canvas units, as the editor never needs sub-pixel placement. */
export type CanvasPosition = { readonly x: number; readonly y: number }

/** Node positions of one artifact in a sidecar, by stable node id. */
export function layoutPositions(layout: Record<string, unknown> | null, artifactId: string): Map<string, CanvasPosition> {
  const artifacts = layout && isRecord(layout.artifacts) ? layout.artifacts : {}
  const entry = Object.hasOwn(artifacts, artifactId) ? artifacts[artifactId] : undefined
  const nodes = isRecord(entry) && isRecord(entry.nodes) ? entry.nodes : {}
  const out = new Map<string, CanvasPosition>()
  for (const [id, node] of Object.entries(nodes)) {
    const p = isRecord(node) && isRecord(node.position) ? node.position : null
    if (p) out.set(id, { x: Number(isLosslessNumber(p.x) ? p.x.value : p.x), y: Number(isLosslessNumber(p.y) ? p.y.value : p.y) })
  }
  return out
}

/**
 * The installed sidecar with one artifact's node positions replaced (other nodes, artifacts, viewport,
 * columns and settings untouched). Addressed by node id, never by array position.
 */
export function withNodePositions(layout: Record<string, unknown> | null, artifactId: string, positions: ReadonlyMap<string, CanvasPosition>): Record<string, unknown> {
  const next = layout ? deepClone(layout) : { layoutVersion: "0.1", artifacts: {} }
  if (!isRecord(next.artifacts)) next.artifacts = {}
  const artifacts = next.artifacts as Record<string, unknown>
  const entry = Object.hasOwn(artifacts, artifactId) && isRecord(artifacts[artifactId]) ? (artifacts[artifactId] as Record<string, unknown>) : {}
  const nodes = isRecord(entry.nodes) ? entry.nodes : {}
  for (const [id, p] of positions) {
    const node = Object.hasOwn(nodes, id) && isRecord(nodes[id]) ? (nodes[id] as Record<string, unknown>) : {}
    const position = { x: new LosslessNumber(String(Math.round(p.x))), y: new LosslessNumber(String(Math.round(p.y))) }
    Object.defineProperty(nodes, id, { value: { ...node, position }, writable: true, enumerable: true, configurable: true })
  }
  Object.defineProperty(artifacts, artifactId, { value: { ...entry, nodes }, writable: true, enumerable: true, configurable: true })
  return next
}

export function serialize(value: unknown): string {
  return stringify(value) ?? "null"
}

/**
 * Cell edits between the table as loaded and the table the editor reports:
 * only string cells of rows that existed when loaded.
 */
export function diffTable(original: unknown, edited: unknown): TableEdit {
  // Null-prototype records: a row id such as `__proto__` is data, not a prototype setter.
  const out: Record<string, Record<string, string>> = Object.create(null) as Record<string, Record<string, string>>
  if (!isRecord(original) || !isRecord(edited)) return out
  const editedRows = new Map(asArray(edited.rules).flatMap((r) => (isRecord(r) && typeof r._id === "string" ? [[r._id, r] as const] : [])))
  const columns = editableColumns(original)
  for (const row of asArray(original.rules)) {
    if (!isRecord(row) || typeof row._id !== "string") continue
    const next = editedRows.get(row._id)
    if (!next) continue
    for (const column of columns) {
      const before = own(row, column)
      const after = own(next, column)
      if (typeof after === "string" && (typeof before === "string" || before === undefined) && after !== (before ?? "")) {
        const cells = out[row._id] ?? (Object.create(null) as Record<string, string>)
        cells[column] = after
        out[row._id] = cells
      }
    }
  }
  return out
}

export function countCells(edits: Readonly<Record<string, TableEdit>>): number {
  return Object.values(edits).reduce((n, table) => n + Object.values(table).reduce((m, row) => m + Object.keys(row).length, 0), 0)
}
